import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useNavigate } from "@tanstack/react-router";
import { useToast } from "@/hooks/use-toast";
import { useDjRestAuthPasswordResetCreate } from "@/api/lctApIComponents";
import { useState } from "react";

export function ForgotPasswordForm() {
  const navigate = useNavigate();
  const [passwordResetSent, setPasswordResetSent] = useState<boolean>(false);
  const { toast } = useToast();
  const resetPasswordMutation = useDjRestAuthPasswordResetCreate({
    onSuccess: () => {
      setPasswordResetSent(true);
    },
    onError(error: unknown) {
      // @ts-expect-error
      const errorMessages: Array<string> = Object.values(error?.stack) ?? [];
      const a = errorMessages.reduce((e: string | null, acc: string) => `${acc} ${e ?? ""}`, "") ?? null;

      toast({
        variant: "destructive",
        title: "Error!",
        description: `${a ? a : "Something went wrong, please try again"}`,
      });
    },
  });

  const formSchema = z.object({
    email: z.string().email(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    resetPasswordMutation.mutate({
      body: { email: values.email },
    });
  };

  if (passwordResetSent) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-6">
        <div className="flex flex-col gap-2 text-center">
          <h1 className="font-heading text-[32px] font-semibold leading-[48px] text-black">Check your email inbox</h1>
          <p className="font-body text-[14px] font-normal leading-[24px] text-gray-800">
            If your account exists, a reset link is on its way
          </p>
        </div>
        <div className="flex h-[100px] w-[100px] items-center justify-center rounded-full bg-[#2d6532]">
          <MailCheck color="white" size={52} />
        </div>
        <Button
          variant="outline"
          onClick={() => navigate({ to: "/login", replace: true })}
          disabled={resetPasswordMutation.isPending}
          className="h-10 w-full rounded-[6px] border border-slate-200 bg-white px-4 py-2 font-body text-sm font-medium leading-6 text-gray-900 hover:bg-gray-50"
        >
          <ArrowLeft className="mr-2" size={16} />
          Back to Log In page
        </Button>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-2 text-center">
        <h1 className="font-heading text-[32px] font-semibold leading-[48px] text-black">Forgot your password?</h1>
        <p className="font-body text-[14px] font-medium leading-[21px] text-slate-700">
          We&apos;ll email you instructions to reset your password.
        </p>
      </div>

      <Form {...form}>
        <form id="forgot-password" onSubmit={form.handleSubmit(onSubmit)} className="flex w-full flex-col gap-6">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1.5">
                <FormLabel className="font-body text-base font-normal leading-7 text-gray-900">Email</FormLabel>
                <FormControl>
                  <Input
                    placeholder="johnsmith@gmail.com"
                    {...field}
                    type="email"
                    autoComplete="email"
                    className="h-12 w-full rounded-[6px] border border-slate-400 bg-white px-3 py-3 font-body text-base font-normal leading-7 text-slate-900"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-col gap-6">
            <Button
              type="submit"
              disabled={resetPasswordMutation.isPending}
              className="h-10 w-full rounded-[6px] bg-[#2d6532] px-4 py-2 font-body text-sm font-semibold leading-6 text-white hover:bg-[#2d6532]/90"
            >
              {resetPasswordMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Reset password
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate({ to: "/login", replace: true })}
              disabled={resetPasswordMutation.isPending}
              className="h-10 w-full rounded-[6px] border border-slate-200 bg-white px-4 py-2 font-body text-sm font-medium leading-6 text-gray-900 hover:bg-gray-50"
            >
              <ArrowLeft className="mr-2" size={16} />
              Back to Log In page
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
